// Community Challenge Feed Item Component
// Displays community challenges and competitions

import React from "react";
import { motion } from "framer-motion";
import {
  FeedItemContent,
  ContentOverlay,
  UserInfo,
  UserAvatar,
  UserDetails,
  Username,
  Timestamp,
  ContentText,
  ContentTitle,
  ContentDescription,
  ActionButtons,
  ActionButton,
  ActionCount,
} from "../../../styles/SocialFeed/FeedContainer.styles";
import { BaseFeedItemProps, FeedInteraction } from "../../../types/socialFeed";

export const CommunityChallengeItem: React.FC<BaseFeedItemProps> = ({
  item,
  onInteraction,
  onUserClick,
  onSwipeModeTrigger,
  showSwipeOption = false,
  isFullScreen = false,
  aspectRatio,
}) => {
  const { content, engagement } = item;

  // Get challenge display
  const getChallengeDisplay = () => {
    if (content.challenge) {
      return {
        icon: "🏆",
        title: content.challenge.title,
        description: content.challenge.description,
        participants: content.challenge.participants,
        type: content.challenge.type,
        color: "#e74c3c",
      };
    }
    return {
      icon: "🏆",
      title: "Community Challenge",
      description: "Join the community challenge!",
      participants: 0,
      type: "weekly",
      color: "#e74c3c",
    };
  };

  const displayData = getChallengeDisplay();

  return (
    <FeedItemContent>
      {/* Background with challenge gradient */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${displayData.color}20 0%, ${displayData.color}40 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Challenge Icon */}
        <motion.div
          style={{
            fontSize: "4rem",
            marginBottom: "1rem",
          }}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            duration: 0.6, 
            delay: 0.2,
            type: "spring",
            stiffness: 200 
          }}
        >
          {displayData.icon}
        </motion.div>
      </motion.div>

      {/* Content Overlay */}
      <ContentOverlay>
        <UserInfo>
          <UserAvatar 
            src={item.avatar} 
            alt={item.username}
            onClick={() => onUserClick(item.userId)}
          />
          <UserDetails>
            <Username onClick={() => onUserClick(item.userId)}>
              {item.username}
            </Username>
            <Timestamp>
              {formatTimestamp(item.timestamp)}
            </Timestamp>
          </UserDetails>
        </UserInfo>

        <ContentText>
          <ContentTitle style={{ color: displayData.color }}>
            {displayData.title}
          </ContentTitle>
          <ContentDescription>
            {displayData.description}
          </ContentDescription>
          
          {/* Challenge Info */}
          <div style={{ 
            marginTop: "0.5rem",
            padding: "0.5rem",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "8px",
            fontSize: "0.8rem",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>
                <strong>Participants:</strong> {displayData.participants}
              </span>
              <span style={{
                padding: "0.25rem 0.5rem",
                background: displayData.color,
                borderRadius: "12px",
                fontSize: "0.7rem",
                fontWeight: "600",
                textTransform: "uppercase",
              }}>
                {displayData.type}
              </span>
            </div>
          </div>
        </ContentText>
      </ContentOverlay>

      {/* Action Buttons */}
      <ActionButtons>
        <ActionButton
          $variant="primary"
          $isActive={engagement.isLiked}
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "like",
            timestamp: new Date(),
          })}
        >
          {engagement.isLiked ? "❤️" : "🤍"}
        </ActionButton>
        <ActionCount>{engagement.likes}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "comment",
            timestamp: new Date(),
          })}
        >
          💬
        </ActionButton>
        <ActionCount>{engagement.comments}</ActionCount>

        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "share",
            timestamp: new Date(),
          })}
        >
          📤
        </ActionButton>
        <ActionCount>{engagement.shares}</ActionCount>

        {/* Join Challenge Button */}
        <ActionButton
          onClick={() => onInteraction({
            itemId: item.id,
            userId: item.userId,
            type: "view",
            timestamp: new Date(),
            metadata: { action: "join-challenge" }
          })}
        >
          🏆
        </ActionButton>
      </ActionButtons>
    </FeedItemContent>
  );
};

// Helper function to format timestamp
const formatTimestamp = (timestamp: Date): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  
  return new Date(timestamp).toLocaleDateString();
};
